import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, XCircle, CheckCircle } from 'lucide-react';
import { auth } from '../services/api';
import PasswordRequirements, { getPasswordStrength } from '../components/PasswordRequirements';

const readTokenFromUrl = () =>
  new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') || '';

const inputClass = 'appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [token] = useState(readTokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [linkInvalid, setLinkInvalid] = useState(!token);

  // Take the token out of the address bar, so it can't be copied or left in
  // browser history once it has been read.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, []);

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setLoading(true);

    try {
      await auth.resetPassword(token, newPassword);
      navigate('/login', {
        replace: true,
        state: { message: 'Senha redefinida com sucesso. Entre com sua nova senha.' }
      });
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'INVALID_RESET_TOKEN') {
        setLinkInvalid(true);
      } else {
        setErrors(data?.errors || [data?.message || 'Erro ao redefinir senha. Por favor, tente novamente.']);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-100 via-brand-50 to-coral-100 py-12 px-4">
      <div className="backdrop-blur-lg bg-white/70 rounded-2xl shadow-2xl p-10 w-full max-w-md flex flex-col items-center">
        <div className="h-16 w-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="h-8 w-8 text-brand-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Redefinir senha</h1>

        {linkInvalid ? (
          <div className="w-full text-center">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              Este link de redefinição é inválido ou expirou.
            </div>
            <Link
              to="/esqueci-minha-senha"
              className="mt-4 inline-block py-2 px-4 rounded-lg bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold"
            >
              Solicitar um novo link
            </Link>
          </div>
        ) : (
          <form className="w-full space-y-5" onSubmit={handleSubmit}>
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-start">
                <XCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <ul className="space-y-1">
                  {errors.map((error) => <li key={error}>{error}</li>)}
                </ul>
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">Nova Senha</label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setErrors([]); }}
                  className={inputClass}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {newPassword && <PasswordRequirements password={newPassword} />}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirmar Nova Senha</label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
              />
              {confirmPassword && (
                <p className={`mt-2 text-sm flex items-center ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                  {passwordsMatch ? <CheckCircle className="h-4 w-4 mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                  {passwordsMatch ? 'As senhas coincidem' : 'As senhas não coincidem'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || strength.score < 100 || !passwordsMatch}
              className="w-full py-3 px-4 rounded-lg bg-brand-700 hover:bg-brand-800 text-white font-semibold shadow-md transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>
          </form>
        )}

        <Link to="/login" className="mt-6 text-sm text-brand-600 hover:text-brand-800 font-medium">
          Voltar para o login
        </Link>
      </div>
    </div>
  );
};

export default ResetPassword;
